import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import {
  Check,
  ClipboardList,
  Clock,
  CreditCard,
  FileSignature,
  HelpCircle,
  History,
  Loader2,
  Mail,
  Pencil,
  Phone,
  ReceiptText,
  Save,
  ShieldCheck,
  Truck,
  UserCog,
  X,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';
import { formatUsd, initials } from '@/lib/format';
import { useAccountSummary } from '@/hooks/useAccountSummary';
import { useAccountChangeRequests } from '@/hooks/useAccountChangeRequests';
import { useBillingAddress } from '@/hooks/useBillingAddress';
import { useCustomer } from '@/hooks/useCustomer';
import { useSession } from '@/lib/auth';
import {
  SUPPORT_MAILTO_HREF,
  SUPPORT_PHONE,
  SUPPORT_PHONE_TOLL_FREE,
  SUPPORT_TOLL_FREE_HREF,
} from '@/lib/constants';
import { ContactUsMenu } from '@/components/layout/ContactUsMenu';
import { EquipmentSection } from '@/components/account/EquipmentSection';
import { FileLibrary } from '@/components/account/FileLibrary';
import { SectionCard } from '@/components/layout/SectionCard';

/**
 * Settings → Profile. The former /account page, folded into the Settings hub.
 *
 * Live:
 *  - Account summary stats + quick actions
 *  - Delivery + billing address cards (with "Request change" modal)
 *  - Leased equipment
 *  - Contracts & files library
 *  - "Trouble signing in" support rails
 *
 * Cylinders moved out (it has its own sidebar nav); team management moved to the
 * Team tab; password change moved to the Security tab.
 */
export default function ProfileTab() {
  const navigate = useNavigate();
  const { hash } = useLocation();
  const { session } = useSession();

  // Deep-link support: the sidebar's Account → Contracts entry navigates to
  // /settings/profile#contracts. Scroll the Contracts & files card into view
  // once it's mounted.
  useEffect(() => {
    if (hash !== '#contracts') return;
    const el = document.getElementById('contracts');
    if (el) {
      const t = window.setTimeout(
        () => el.scrollIntoView({ behavior: 'smooth', block: 'start' }),
        100,
      );
      return () => window.clearTimeout(t);
    }
  }, [hash]);
  const { data: ctx } = useCustomer();
  const { data: summary, isLoading: summaryLoading } = useAccountSummary();
  const { data: billing } = useBillingAddress();

  const customerName = ctx?.customer.name ?? 'Your account';
  const fullName = ctx?.user.fullName ?? session?.user?.email ?? 'Customer';
  const email = session?.user?.email ?? '';
  const role =
    ctx?.user.role === 'ap'
      ? 'Accounts Payable'
      : ctx?.user.role === 'admin'
        ? 'Admin'
        : 'Member';

  const primaryLocation = ctx?.locations[0] ?? null;
  const otherLocations = ctx?.locations.slice(1) ?? [];

  const [addressEdit, setAddressEdit] = useState<'delivery' | 'billing' | null>(null);

  return (
    <div className="space-y-6">
      {/* The Account "document" — navy header band with the customer name */}
      <div className="rounded-2xl border-2 border-brix-primary-deep bg-brix-surface overflow-hidden shadow-card-deep">
        <div className="bg-brix-primary-deep px-6 md:px-8 py-5 flex items-center gap-4">
          <div
            className="w-12 h-12 rounded-full bg-red-600 text-white flex items-center justify-center text-base font-bold shadow-card relative overflow-hidden shrink-0"
            aria-hidden
          >
            <span className="absolute inset-0 bg-gradient-to-br from-white/30 to-transparent pointer-events-none" />
            <span className="relative">{initials(fullName)}</span>
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-xs tracking-tight text-white/70 font-semibold">Account</p>
            <h2 className="font-display text-xl md:text-2xl font-bold tracking-tight text-white leading-tight mt-0.5 break-words">
              {customerName}
            </h2>
            <p className="text-xs text-white/80 tracking-tight mt-1">
              Signed in as <span className="font-semibold text-white">{fullName}</span>
              {email && (
                <>
                  {' '}
                  · <span className="text-white/70">{email}</span>
                </>
              )}{' '}
              · <span className="text-white font-semibold">{role}</span>
            </p>
          </div>
        </div>

        {/* Account summary stats */}
        <div className="px-6 md:px-8 py-5 grid grid-cols-2 md:grid-cols-3 gap-4 border-b border-brix-border bg-brix-surface-alt/40">
          {summaryLoading ? (
            <div className="col-span-3 flex items-center gap-2 text-sm text-slate-900 tracking-tight">
              <Loader2 size={14} className="animate-spin" /> Loading…
            </div>
          ) : !summary ? (
            <p className="col-span-3 text-sm text-slate-900 tracking-tight">
              Summary unavailable.
            </p>
          ) : (
            <>
              <Stat label="Open balance" value={formatUsd(summary.openBalance)} />
              <Stat
                label="Past due"
                value={formatUsd(summary.pastDueBalance)}
                tone={summary.pastDueBalance > 0 ? 'danger' : 'success'}
              />
              <Stat label="Open invoices" value={summary.openInvoiceCount.toString()} />
              <Stat label="Payment terms" value={summary.paymentTerms} />
              <Stat
                label="Credit limit"
                value={summary.creditLimit !== null ? formatUsd(summary.creditLimit) : '—'}
              />
              <Stat
                label="Credit hold"
                value={summary.onCreditHold ? 'On hold' : 'Clear'}
                tone={summary.onCreditHold ? 'danger' : 'success'}
              />
            </>
          )}
        </div>

        {/* Quick actions */}
        <div className="px-6 md:px-8 py-5 flex gap-3 flex-wrap">
          <Button type="button" size="lg" onClick={() => navigate('/invoices')}>
            <ReceiptText size={16} /> Invoices
          </Button>
          <Button
            type="button"
            size="lg"
            variant="secondary"
            onClick={() => navigate('/order?tab=history')}
          >
            <ClipboardList size={16} /> Order history
          </Button>
          <Button type="button" size="lg" variant="secondary" onClick={() => navigate('/order')}>
            <Truck size={16} /> Place an order
          </Button>
        </div>
      </div>

      {/* Editable personal profile — display name + phone for the signed-in user */}
      <PersonalProfileCard
        initialName={ctx?.user.fullName ?? ''}
        initialPhone={ctx?.user.phone ?? ''}
        signedIn={Boolean(ctx)}
      />

      {/* Delivery + Billing address — two cards side by side on md+ */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <AddressCard
          kind="delivery"
          title="Delivery address"
          icon={<Truck size={18} />}
          lines={
            primaryLocation
              ? [
                  primaryLocation.displayName ?? '',
                  primaryLocation.addressLine1 ?? '',
                  [
                    primaryLocation.city ?? '',
                    [primaryLocation.state, primaryLocation.zip].filter(Boolean).join(' '),
                  ]
                    .filter(Boolean)
                    .join(', '),
                ].filter(Boolean)
              : []
          }
          notes={primaryLocation?.deliveryNotes ?? null}
          extraLocations={otherLocations.length}
          onRequestChange={() => setAddressEdit('delivery')}
        />
        <AddressCard
          kind="billing"
          title="Billing address"
          icon={<CreditCard size={18} />}
          lines={
            billing
              ? [
                  billing.billingContactName ?? '',
                  billing.line1 ?? '',
                  [
                    billing.city ?? '',
                    [billing.state, billing.postal].filter(Boolean).join(' '),
                  ]
                    .filter(Boolean)
                    .join(', '),
                ].filter(Boolean)
              : []
          }
          notes={billing?.billingEmail ? `Invoices emailed to ${billing.billingEmail}` : null}
          extraLocations={0}
          onRequestChange={() => setAddressEdit('billing')}
        />
      </div>

      {/* Pending account-info change requests — self-hides when none on file. */}
      <PendingChangesCard />

      {/* Leased equipment — self-hides when no rentals on file. */}
      <EquipmentSection />

      {/* Contracts & files */}
      <SectionCard
        id="contracts"
        icon={<FileSignature size={18} />}
        title="Contracts & files"
        subtitle="Signed contracts, equipment leases, and any other files we share with you."
      >
        {!ctx ? (
          <div className="flex items-center gap-2 text-sm text-slate-900 tracking-tight">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        ) : (
          <FileLibrary customerId={ctx.customer.id} />
        )}
      </SectionCard>

      {/* Trouble signing in / contact rails */}
      <SectionCard
        danger
        icon={<HelpCircle size={18} />}
        title="Trouble signing in?"
        subtitle="Reach the Brix team — pick whichever channel works best."
      >
        <div className="flex flex-wrap justify-center gap-3">
          <ContactUsMenu variant="inline" triggerLabel="Chat with us" initialChannel="email" />
          <a
            href={SUPPORT_MAILTO_HREF}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-brix-border bg-brix-surface text-brix-primary font-semibold text-sm shadow-card hover:shadow-card-hover hover:-translate-y-0.5 transition-all duration-200 cursor-pointer"
          >
            <Mail size={15} /> Email us
          </a>
          <a
            href={SUPPORT_TOLL_FREE_HREF}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-brix-border bg-brix-surface text-brix-primary font-semibold text-sm shadow-card hover:shadow-card-hover hover:-translate-y-0.5 transition-all duration-200 cursor-pointer"
          >
            <Phone size={15} /> Call {SUPPORT_PHONE_TOLL_FREE}
          </a>
        </div>
        <p className="text-xs text-slate-900/70 tracking-tight pt-3 text-center">
          Or call us locally{' '}
          <a
            href={`tel:${SUPPORT_PHONE.replace(/[^0-9+]/g, '')}`}
            className="text-brix-primary underline"
          >
            {SUPPORT_PHONE}
          </a>
          .
        </p>
      </SectionCard>

      {addressEdit && ctx && (
        <AddressChangeModal
          kind={addressEdit}
          locationId={primaryLocation?.id ?? null}
          initial={
            addressEdit === 'delivery'
              ? {
                  line1: primaryLocation?.addressLine1 ?? '',
                  city: primaryLocation?.city ?? '',
                  state: primaryLocation?.state ?? '',
                  postal: primaryLocation?.zip ?? '',
                  delivery_notes: primaryLocation?.deliveryNotes ?? '',
                }
              : {
                  contact_name: billing?.billingContactName ?? '',
                  email: billing?.billingEmail ?? '',
                  line1: billing?.line1 ?? '',
                  city: billing?.city ?? '',
                  state: billing?.state ?? '',
                  postal: billing?.postal ?? '',
                }
          }
          onClose={() => setAddressEdit(null)}
        />
      )}
    </div>
  );
}

/* ----------------------------- helpers ----------------------------- */

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'success' | 'danger';
}) {
  const color =
    tone === 'danger'
      ? 'text-red-600'
      : tone === 'success'
        ? 'text-emerald-700'
        : 'text-slate-900';
  return (
    <div>
      <div className="text-sm tracking-tight text-slate-900 font-semibold">{label}</div>
      <div
        className={cn('mt-1 text-2xl font-bold tracking-tight', color)}
        style={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {value}
      </div>
    </div>
  );
}

/**
 * Pending / recent account-info change requests for the active customer. RLS
 * scopes the rows to the caller's customer. Self-hides when there are none.
 * Does NOT select token columns (the hook only reads safe columns).
 */
function PendingChangesCard() {
  const { data: requests, isLoading } = useAccountChangeRequests();
  if (isLoading || !requests || requests.length === 0) return null;

  const labelForRequest = (kind: 'address' | 'contact') =>
    kind === 'address' ? 'Delivery address' : 'Company contact / billing';

  return (
    <SectionCard
      icon={<History size={18} />}
      title="Pending change requests"
      subtitle="Address & contact changes you’ve submitted for approval by the Brix team."
    >
      <div className="space-y-2">
        {requests.map((r) => (
          <div
            key={r.id}
            className="flex items-center justify-between gap-3 rounded-xl border border-brix-border bg-brix-surface px-4 py-3 flex-wrap"
          >
            <div className="min-w-0">
              <div className="text-sm font-semibold text-slate-900 tracking-tight">
                {labelForRequest(r.kind)}
              </div>
              <div className="text-[11px] text-slate-900/60 tracking-tight">
                Submitted {new Date(r.createdAt).toLocaleDateString()}
                {r.reviewedAt
                  ? ` · reviewed ${new Date(r.reviewedAt).toLocaleDateString()}`
                  : ''}
              </div>
            </div>
            <ChangeStatusPill status={r.status} />
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

function ChangeStatusPill({ status }: { status: 'pending' | 'applied' | 'rejected' }) {
  if (status === 'applied')
    return (
      <StatusBadge tone="success" size="sm" icon={<Check size={11} />}>
        Applied
      </StatusBadge>
    );
  if (status === 'rejected')
    return (
      <StatusBadge tone="neutral" size="sm" icon={<X size={11} />}>
        Rejected
      </StatusBadge>
    );
  return (
    <StatusBadge tone="warning" size="sm" icon={<Clock size={11} />}>
      Pending approval
    </StatusBadge>
  );
}

/**
 * Editable display name + phone for the signed-in user's OWN profile.
 *
 * Writes go through /.netlify/functions/customer-update-profile (service-role,
 * scoped to the caller's auth uid) because orders.customer_users has no UPDATE
 * grant/RLS policy for the browser anon/authenticated roles. On success we
 * invalidate the customer-context query so the header here, the sidebar, and
 * the account switcher all pick up the new name.
 */
function PersonalProfileCard({
  initialName,
  initialPhone,
  signedIn,
}: {
  initialName: string;
  initialPhone: string;
  signedIn: boolean;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState(initialName);
  const [phone, setPhone] = useState(initialPhone);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{ kind: 'ok' | 'err'; msg: string } | null>(null);

  // Re-seed the fields when the underlying context loads/changes (e.g. on the
  // account switcher flipping customers, or the initial query resolving).
  useEffect(() => {
    setName(initialName);
    setPhone(initialPhone);
  }, [initialName, initialPhone]);

  const trimmedName = name.trim();
  const trimmedPhone = phone.trim();
  const dirty = trimmedName !== initialName.trim() || trimmedPhone !== initialPhone.trim();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (saving) return;
    setFeedback(null);

    if (trimmedName.length === 0) {
      setFeedback({ kind: 'err', msg: 'Please enter your name.' });
      return;
    }

    setSaving(true);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess.session?.access_token;
      if (!token) throw new Error('You are not signed in.');

      const res = await fetch('/.netlify/functions/customer-update-profile', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          full_name: trimmedName,
          phone: trimmedPhone.length > 0 ? trimmedPhone : null,
        }),
      });
      const body = (await res.json().catch(() => ({}))) as {
        ok?: boolean;
        error?: string;
      };
      if (!res.ok || !body.ok) {
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }

      // Refresh the customer context so the name updates everywhere it's shown.
      await qc.invalidateQueries({ queryKey: ['customer-context'] });
      setFeedback({ kind: 'ok', msg: 'Profile updated.' });
    } catch (err) {
      setFeedback({
        kind: 'err',
        msg: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <SectionCard
      icon={<UserCog size={18} />}
      title="Your profile"
      subtitle="Update the name and phone number we use to reach you."
    >
      <form onSubmit={handleSubmit} className="max-w-md space-y-4">
        <div>
          <label
            htmlFor="profile-name"
            className="text-xs tracking-tight font-semibold text-slate-900 block"
          >
            Display name
          </label>
          <div className="mt-1">
            <Input
              id="profile-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              autoComplete="name"
              maxLength={200}
              disabled={!signedIn || saving}
              required
            />
          </div>
        </div>

        <div>
          <label
            htmlFor="profile-phone"
            className="text-xs tracking-tight font-semibold text-slate-900 block"
          >
            Phone <span className="font-normal text-slate-900/60">(optional)</span>
          </label>
          <div className="mt-1">
            <Input
              id="profile-phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="(510) 555-0142"
              autoComplete="tel"
              maxLength={50}
              disabled={!signedIn || saving}
            />
          </div>
        </div>

        {feedback && (
          <div
            className={cn(
              'text-xs tracking-tight',
              feedback.kind === 'ok' ? 'text-emerald-700' : 'text-red-600',
            )}
          >
            {feedback.msg}
          </div>
        )}

        <Button type="submit" size="sm" disabled={!signedIn || saving || !dirty}>
          {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </form>
    </SectionCard>
  );
}

function AddressCard({
  kind,
  title,
  icon,
  lines,
  notes,
  extraLocations,
  onRequestChange,
}: {
  kind: 'delivery' | 'billing';
  title: string;
  icon: ReactNode;
  lines: string[];
  notes: string | null;
  extraLocations: number;
  onRequestChange: () => void;
}) {
  return (
    <div className="rounded-2xl border-2 border-brix-primary-deep bg-brix-surface overflow-hidden shadow-card-deep">
      <div className="bg-brix-primary-deep px-5 md:px-6 py-3.5 flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-white/15 flex items-center justify-center text-white shrink-0">
          {icon}
        </div>
        <h3 className="font-display text-lg font-bold tracking-tight text-white leading-none">
          {title}
        </h3>
      </div>
      <div className="px-5 md:px-6 pt-5 pb-5">
        <div className="space-y-0.5">
          {lines.length === 0 ? (
            <p className="text-sm text-slate-900/70 italic tracking-tight">
              No {kind === 'billing' ? 'billing' : 'delivery'} address on file.
            </p>
          ) : (
            lines.map((l, i) => (
              <p
                key={`${l}-${i}`}
                className={cn(
                  'text-sm tracking-tight',
                  i === 0 ? 'font-bold text-slate-900' : 'text-slate-900',
                )}
              >
                {l}
              </p>
            ))
          )}
          {notes && (
            <p className="text-xs text-slate-900 tracking-tight mt-2 italic">{notes}</p>
          )}
          {extraLocations > 0 && (
            <p className="text-xs text-slate-900 tracking-tight mt-2">
              + {extraLocations} other location
              {extraLocations === 1 ? '' : 's'} on file
            </p>
          )}
        </div>
        <div className="mt-4">
          <Button type="button" variant="secondary" size="sm" onClick={onRequestChange}>
            <Pencil size={12} /> Request change
          </Button>
        </div>
      </div>
    </div>
  );
}

interface AddressChangeModalProps {
  kind: 'delivery' | 'billing';
  locationId: string | null;
  initial: Record<string, string>;
  onClose: () => void;
}

function AddressChangeModal({
  kind,
  locationId,
  initial,
  onClose,
}: AddressChangeModalProps) {
  const qc = useQueryClient();
  const [contactName, setContactName] = useState(initial.contact_name ?? '');
  const [contactEmail, setContactEmail] = useState(initial.email ?? '');
  const [line1, setLine1] = useState(initial.line1 ?? '');
  const [city, setCity] = useState(initial.city ?? '');
  const [state, setState] = useState(initial.state ?? '');
  const [postal, setPostal] = useState(initial.postal ?? '');
  const [deliveryNotes, setDeliveryNotes] = useState(initial.delivery_notes ?? '');
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{
    kind: 'ok' | 'err';
    msg: string;
    qboPending?: boolean;
  } | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !saving) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, saving]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (saving) return;
    setFeedback(null);
    setSaving(true);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess.session?.access_token;
      if (!token) throw new Error('not signed in');

      // Build the proposed change in the new request shape. Nothing is applied
      // on submit — this parks a PENDING request and emails the approver.
      let requestKind: 'address' | 'contact';
      const proposed: Record<string, string | null> = {};
      if (kind === 'delivery') {
        requestKind = 'address';
        proposed.line1 = line1.trim();
        proposed.city = city.trim();
        proposed.state = state.trim();
        proposed.zip = postal.trim();
        proposed.delivery_notes = deliveryNotes.trim() || null;
      } else {
        requestKind = 'contact';
        proposed.billing_contact_name = contactName.trim() || null;
        proposed.billing_email = contactEmail.trim() || null;
        proposed.line1 = line1.trim();
        proposed.city = city.trim();
        proposed.state = state.trim();
        proposed.postal = postal.trim();
      }

      const res = await fetch('/.netlify/functions/customer-request-account-change', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          kind: requestKind,
          location_id: kind === 'delivery' ? locationId : null,
          proposed,
        }),
      });
      const body = (await res.json().catch(() => ({}))) as {
        ok?: boolean;
        error?: string;
        emailed?: boolean;
      };
      if (!res.ok || !body.ok) {
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }

      setFeedback({
        kind: 'ok',
        msg:
          'Sent to service@brixbev.com for approval — you’ll see it update once approved.',
        qboPending: false,
      });
      qc.invalidateQueries({ queryKey: ['account-change-requests'] });
      setTimeout(onClose, 2200);
    } catch (err) {
      setFeedback({
        kind: 'err',
        msg: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setSaving(false);
    }
  }

  const title = kind === 'billing' ? 'Update billing address' : 'Update delivery address';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="addr-change-heading"
      className="fixed inset-0 z-[110] flex items-start md:items-center justify-center px-4 py-8 overflow-y-auto"
    >
      <div
        aria-hidden
        className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm"
        onClick={() => !saving && onClose()}
      />
      <div className="relative w-full max-w-md rounded-2xl bg-white shadow-card-deep overflow-hidden border-2 border-brix-primary-deep">
        <form onSubmit={submit}>
          <div className="bg-brix-primary-deep px-6 py-4 flex items-center justify-between gap-3">
            <h2
              id="addr-change-heading"
              className="font-display text-lg font-bold tracking-tight text-white leading-none"
            >
              {title}
            </h2>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              disabled={saving}
              className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full text-white hover:bg-white/15 cursor-pointer disabled:opacity-50"
            >
              <X size={18} />
            </button>
          </div>

          <div className="px-6 py-5 space-y-3 text-sm text-slate-900 tracking-tight">
            {kind === 'billing' && (
              <>
                <Field label="Contact name">
                  <Input
                    value={contactName}
                    onChange={(e) => setContactName(e.target.value)}
                    placeholder="A/P contact"
                  />
                </Field>
                <Field label="Invoices emailed to">
                  <Input
                    type="email"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    placeholder="ap@yourcompany.com"
                  />
                </Field>
              </>
            )}
            <Field label="Street address">
              <Input
                value={line1}
                onChange={(e) => setLine1(e.target.value)}
                placeholder="1951 Monarch Street"
                required
              />
            </Field>
            <div className="grid grid-cols-[1fr_72px_120px] gap-2">
              <Field label="City">
                <Input value={city} onChange={(e) => setCity(e.target.value)} required />
              </Field>
              <Field label="State">
                <Input
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  maxLength={2}
                  required
                />
              </Field>
              <Field label="ZIP">
                <Input value={postal} onChange={(e) => setPostal(e.target.value)} required />
              </Field>
            </div>
            {kind === 'delivery' && (
              <Field label="Delivery notes (optional)">
                <Input
                  value={deliveryNotes}
                  onChange={(e) => setDeliveryNotes(e.target.value)}
                  placeholder="Loading dock around back, ring buzzer 2"
                />
              </Field>
            )}

            {feedback && (
              <div
                className={cn(
                  'text-xs tracking-tight px-3 py-2 rounded-lg',
                  feedback.kind === 'ok'
                    ? 'bg-emerald-50 border border-emerald-200 text-emerald-800'
                    : 'bg-red-50 border border-red-200 text-red-700',
                )}
              >
                {feedback.msg}
              </div>
            )}

            <p className="text-[11px] text-slate-900/70 italic flex items-start gap-1.5 pt-1">
              <ShieldCheck size={11} className="text-emerald-700 mt-0.5 shrink-0" />
              Changes are reviewed by the Brix team before they take effect. We’ll send this to
              service@brixbev.com for approval, then apply it to your account and Service Fusion.
            </p>
          </div>

          <div className="px-6 py-4 border-t border-brix-border bg-brix-surface-alt/40 flex justify-between gap-2">
            <Button type="button" variant="secondary" size="sm" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={saving}>
              {saving ? <Loader2 size={13} className="animate-spin" /> : <Pencil size={13} />}
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-xs tracking-tight font-semibold text-slate-900 block">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
